import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  input,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';

import { Model } from 'survey-core';
import { SurveyModule } from 'survey-angular-ui';

import { FormResponseService } from '../../services';
import { FormStatus } from '../../enums';
import { ToastMessageService } from '@fitness-force/api';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'lib-form-runner',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonModule, SurveyModule],
  templateUrl: './form-runner.component.html',
  styleUrls: ['./form-runner.component.scss'],
})
export class FormRunnerComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly responseService = inject(FormResponseService);
  private readonly toastService = inject(ToastMessageService);

  // Route parameters (for public form access)
  companyUuid = input<string>('');
  tenantUuid = input<string>('');
  responseUuid = input<string>('');

  // Survey model
  surveyModel!: Model;

  // State
  loading = signal(true);
  formExpired = signal(false);
  formCompleted = signal(false);
  savingDraft = signal(false);

  // Auto-save subscription
  private autoSaveSubscription?: Subscription;
  private readonly AUTO_SAVE_INTERVAL = 30000; // 30 seconds

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.stopAutoSave();
  }

  initializeComponent(): void {
    // Get route parameters
    const companyId =
      this.route.snapshot.paramMap.get('companyId') || this.companyUuid();
    const tenantId =
      this.route.snapshot.paramMap.get('tenantId') || this.tenantUuid();
    const responseId =
      this.route.snapshot.paramMap.get('responseId') || this.responseUuid();

    if (!companyId || !tenantId || !responseId) {
      this.toastService.showErrorMessage('Invalid form link');
      return;
    }

    this.loadFormResponse(companyId, tenantId, responseId);
  }

  loadFormResponse(
    companyUuid: string,
    tenantUuid: string,
    responseUuid: string,
  ): void {
    this.responseService
      .getPublicFormResponse(companyUuid, tenantUuid, responseUuid)
      .subscribe({
        next: (response) => {
          // Check if form is expired (API: is_expired or expiry_date)
          if (response.is_expired) {
            this.formExpired.set(true);
            this.loading.set(false);
            return;
          }
          if (response.expiry_date) {
            const expiryDate = new Date(response.expiry_date);
            if (
              expiryDate < new Date() &&
              response.status !== FormStatus.COMPLETED
            ) {
              this.formExpired.set(true);
              this.loading.set(false);
              return;
            }
          }

          // Check if form is already completed (API: is_completed or status)
          if (response.is_completed || response.status === FormStatus.COMPLETED) {
            this.formCompleted.set(true);
            this.loading.set(false);
            return;
          }

          // Initialize survey (API: template_schema, current_data)
          this.initializeSurvey(response.template_schema, response.current_data);
          this.setupAutoSave(companyUuid, tenantUuid, responseUuid);
          this.loading.set(false);
        },
        error: (error: any) => {
          this.toastService.showError(error);
          this.loading.set(false);
        },
      });
  }

  initializeSurvey(schema: any, currentData?: any): void {
    this.surveyModel = new Model(schema);

    // Load existing data if available (draft mode)
    if (currentData) {
      this.surveyModel.data = currentData;
    }

    // Handle survey completion
    this.surveyModel.onComplete.add((sender) => {
      this.onSurveyComplete(sender.data);
    });

    // Handle page navigation for auto-save
    this.surveyModel.onCurrentPageChanged.add(() => {
      this.saveDraft();
    });

    // Handle value changes for validation
    this.surveyModel.onValueChanged.add(() => {
      // You can add custom validation logic here
    });
  }

  setupAutoSave(
    companyUuid: string,
    tenantUuid: string,
    responseUuid: string,
  ): void {
    this.autoSaveSubscription = interval(this.AUTO_SAVE_INTERVAL).subscribe(
      () => {
        if (this.surveyModel && !this.formCompleted()) {
          this.saveDraft(companyUuid, tenantUuid, responseUuid);
        }
      },
    );
  }

  saveDraft(
    companyUuid?: string,
    tenantUuid?: string,
    responseUuid?: string,
  ): void {
    if (this.savingDraft()) return;

    const company =
      companyUuid ||
      this.route.snapshot.paramMap.get('companyId') ||
      this.companyUuid();
    const tenant =
      tenantUuid ||
      this.route.snapshot.paramMap.get('tenantId') ||
      this.tenantUuid();
    const response =
      responseUuid ||
      this.route.snapshot.paramMap.get('responseId') ||
      this.responseUuid();

    if (!company || !tenant || !response) return;

    this.savingDraft.set(true);
    const surveyData = this.surveyModel.data;

    this.responseService
      .saveDraft(company, tenant, response, { responseJson: surveyData })
      .subscribe({
        next: () => {
          // Draft saved silently
          this.savingDraft.set(false);
        },
        error: (error: any) => {
          console.error('Auto-save failed:', error);
          this.savingDraft.set(false);
        },
      });
  }

  onSurveyComplete(surveyData: any): void {
    const companyId =
      this.route.snapshot.paramMap.get('companyId') || this.companyUuid();
    const tenantId =
      this.route.snapshot.paramMap.get('tenantId') || this.tenantUuid();
    const responseId =
      this.route.snapshot.paramMap.get('responseId') || this.responseUuid();

    if (!companyId || !tenantId || !responseId) return;

    this.stopAutoSave();

    this.responseService
      .submitComplete(companyId, tenantId, responseId, {
        responseJson: surveyData,
      })
      .subscribe({
        next: () => {
          this.formCompleted.set(true);
          this.toastService.showSuccess('Form submitted successfully!');
        },
        error: (error: any) => {
          this.toastService.showError(error);
        },
      });
  }

  stopAutoSave(): void {
    if (this.autoSaveSubscription) {
      this.autoSaveSubscription.unsubscribe();
    }
  }
}
